/* global __firebase_config, __app_id, __initial_auth_token */
import React, { useState, useRef, useEffect } from 'react';
import { Compass, Lock, Map as MapIcon, Image as ImageIcon, Send, X, AlertCircle, MessageCircle, Edit2, Check, Bell, BellOff } from 'lucide-react';
import { initializeApp } from 'firebase/app';
import { getAuth, signInWithCustomToken, signInAnonymously, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, addDoc, onSnapshot, doc, updateDoc } from 'firebase/firestore';

// --- Daily Quotes Collection ---
const dailyQuotes = [
  { text: "The mountains are calling and I must go.", author: "John Muir" },
  { text: "Discipline is the bridge between goals and accomplishment.", author: "Jim Rohn" },
  { text: "You have power over your mind - not outside events. Realize this, and you will find strength.", author: "Marcus Aurelius" },
  { text: "Trust in the Lord with all your heart, and do not lean on your own understanding.", author: "Proverbs 3:5" },
  { text: "The Lord is my shepherd; I shall not want.", author: "Psalm 23:1" },
  { text: "In every walk with nature one receives far more than he seeks.", author: "John Muir" },
  { text: "Motivation is what gets you started. Habit is what keeps you going.", author: "Jim Rohn" },
  { text: "He who fears death will never do anything worthy of a man who is alive.", author: "Seneca" },
  { text: "As iron sharpens iron, so one person sharpens another.", author: "Proverbs 27:17" },
  { text: "Be still, and know that I am God.", author: "Psalm 46:10" },
  { text: "Between every two pines is a doorway to a new world.", author: "John Muir" },
  { text: "Success is nothing more than a few simple disciplines, practiced every day.", author: "Jim Rohn" },
  { text: "Waste no more time arguing what a good man should be. Be one.", author: "Marcus Aurelius" },
  { text: "A joyful heart is good medicine, but a crushed spirit dries up the bones.", author: "Proverbs 17:22" },
  { text: "Thy word is a lamp unto my feet, and a light unto my path.", author: "Psalm 119:105" },
  { text: "The clearest way into the Universe is through a forest wilderness.", author: "John Muir" },
  { text: "Don't wish it were easier, wish you were better.", author: "Jim Rohn" },
  { text: "It is not the man who has too little, but the man who craves more, that is poor.", author: "Seneca" },
  { text: "Commit your work to the Lord, and your plans will be established.", author: "Proverbs 16:3" },
  { text: "Create in me a clean heart, O God, and renew a right spirit within me.", author: "Psalm 51:10" }
];

const getQuoteOfTheDay = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 0);
  const diff = now - start;
  const oneDay = 1000 * 60 * 60 * 24;
  const dayOfYear = Math.floor(diff / oneDay);
  return dailyQuotes[dayOfYear % dailyQuotes.length];
};

// --- Firebase Initialization with Safety Fallbacks ---
const getFirebaseConfig = () => {
  try {
    if (typeof window !== 'undefined' && window.__firebase_config) {
      return typeof window.__firebase_config === 'string' 
        ? JSON.parse(window.__firebase_config) 
        : window.__firebase_config;
    }
    if (typeof __firebase_config !== 'undefined' && __firebase_config) {
      return typeof __firebase_config === 'string' 
        ? JSON.parse(__firebase_config) 
        : __firebase_config;
    }
  } catch (e) {
    console.warn("Firebase config parsing failed, using default fallback.");
  }
  
  // Safe default public database fallback.
  // Successfully updated with your brand-new Firebase credentials!
  return {
    apiKey: "AIzaSyDsssxVk5K8k4_BSqW_6sdmxVMIN6z-G1M", 
    authDomain: "true-north-63ec6.firebaseapp.com",
    projectId: "true-north-63ec6",
    storageBucket: "true-north-63ec6.firebasestorage.app",
    messagingSenderId: "217983464081",
    appId: "1:217983464081:web:6f71825e1242e5d80fc530"
  };
};

let app;
let auth;
let db;
let firebaseInitError = null;

try {
  const firebaseConfig = getFirebaseConfig();
  if (!firebaseConfig || !firebaseConfig.apiKey || firebaseConfig.apiKey === "YOUR_FIREBASE_API_KEY" || firebaseConfig.apiKey === "") {
    throw new Error("Missing Firebase Credentials. For live site deployment, paste your free Firebase project keys into the 'getFirebaseConfig' function.");
  }
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);
  db = getFirestore(app);
} catch (error) {
  console.error("Firebase Initialization Failed:", error);
  firebaseInitError = error.message || error;
}

const getAppId = () => {
  if (typeof window !== 'undefined' && window.__app_id) return window.__app_id;
  if (typeof __app_id !== 'undefined' && __app_id) return __app_id;
  return 'true-north-quest-prod';
};
const appId = getAppId();

const getInitialAuthToken = () => {
  if (typeof window !== 'undefined' && window.__initial_auth_token) return window.__initial_auth_token;
  if (typeof __initial_auth_token !== 'undefined' && __initial_auth_token) return __initial_auth_token;
  return null;
};

export default function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    // Keep user logged in during their session so refreshes don't constantly lock them out
    return sessionStorage.getItem('tn_unlocked') === 'true';
  });
  const [user, setUser] = useState(null);

  // RULE 3: Auth First & onAuthStateChanged tracking
  useEffect(() => {
    if (firebaseInitError) return;

    const initAuth = async () => {
      try {
        const token = getInitialAuthToken();
        if (token) {
          await signInWithCustomToken(auth, token);
        } else {
          await signInAnonymously(auth);
        }
      } catch (error) {
        console.error("Firebase Authentication failed:", error);
      }
    };
    initAuth();

    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  const handleUnlock = () => {
    sessionStorage.setItem('tn_unlocked', 'true');
    setIsLoggedIn(true);
  };

  const handleLock = () => {
    sessionStorage.removeItem('tn_unlocked');
    setIsLoggedIn(false);
  };

  // Render setup assistance wizard if configuration keys are missing on the live host
  if (firebaseInitError) {
    return (
      <div className="fixed inset-0 bg-slate-950 flex items-center justify-center p-4 overflow-y-auto font-sans">
        <div className="max-w-md w-full bg-slate-900 border border-slate-800 rounded-3xl p-6 text-slate-200 shadow-2xl relative">
          <div className="absolute inset-0 bg-gradient-to-b from-amber-500/5 to-transparent rounded-3xl pointer-events-none" />
          
          <div className="relative z-10 text-center">
            <div className="w-16 h-16 bg-amber-500/10 border border-amber-500/30 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-lg">
              <Compass className="w-8 h-8 text-amber-500 animate-pulse" />
            </div>
            
            <h2 className="text-2xl font-bold text-white mb-2 tracking-wide uppercase">Connect Cloud Database</h2>
            <p className="text-sm text-slate-400 mb-6 leading-relaxed">
              Your app compiled successfully! To synchronize daily logs and replies with your users on <span className="text-amber-400 font-mono">app.truenorth.quest</span>, connect your free Google Firebase project.
            </p>

            <div className="bg-slate-950/80 rounded-2xl p-4 text-left border border-slate-850 space-y-4 mb-6">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-amber-500">Quick 3-Step Setup Instructions:</h3>
              <ol className="text-xs text-slate-300 space-y-3 list-decimal list-inside leading-relaxed">
                <li>Sign in at <a href="https://console.firebase.google.com" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:underline">firebase.google.com</a> and click <strong>Add Project</strong>.</li>
                <li>Add a <strong>Web App (</strong ><code className="text-amber-400">&lt;/&gt;</code> icon), name it, and copy your <code className="text-amber-300">firebaseConfig</code> keys.</li>
                <li>Turn on your database & login:
                  <ul className="list-disc list-inside pl-4 mt-1.5 space-y-1 text-slate-400">
                    <li>Click <strong>Firestore Database</strong> &gt; <strong>Create Database</strong> (start in Test Mode).</li>
                    <li>Click <strong>Authentication</strong> &gt; <strong>Sign-in method</strong> &gt; Enable <strong>Anonymous</strong>.</li>
                  </ul>
                </li>
              </ol>
            </div>

            <p className="text-xs text-slate-500 leading-relaxed mb-6">
              Open your local <code className="bg-slate-950 px-1 py-0.5 rounded text-slate-400">src/App.js</code> file in VS Code, paste your keys inside <code className="text-amber-400">getFirebaseConfig</code>, save, and run <code className="bg-slate-950 px-1 py-0.5 rounded text-amber-400">npm run build</code>!
            </p>

            <div className="text-xs border-t border-slate-800/60 pt-4 text-slate-400 italic">
              Note: This database wizard displays safely on your live server to prevent a white screen crash.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black sm:p-4 flex justify-center items-center font-sans">
      <div className="w-full max-w-md h-full sm:h-[85vh] bg-slate-900 sm:rounded-3xl sm:border border-slate-700 overflow-hidden relative shadow-2xl flex flex-col">
        {isLoggedIn ? (
          <FeedScreen user={user} onLogout={handleLock} />
        ) : (
          <LoginScreen onLogin={handleUnlock} />
        )}
      </div>
    </div>
  );
}

// --- Login Screen Component ---
const LoginScreen = ({ onLogin }) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const quoteOfDay = getQuoteOfTheDay();

  const handleSubmit = (e) => {
    e.preventDefault();
    const sanitizedCode = code.trim();
    if (sanitizedCode === '1221') {
      onLogin();
    } else {
      setError('Invalid access code. The path remains hidden.');
    }
  };

  return (
    <div className="flex-1 flex flex-col items-center justify-center p-6 text-slate-200 relative overflow-y-auto">
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-950/20 pointer-events-none" />
      
      <div className="relative z-10 flex flex-col items-center w-full min-h-full py-8 justify-center">
        <div className="mb-6 p-4 bg-amber-500/10 rounded-full border border-amber-500/30 shadow-[0_0_30px_rgba(245,158,11,0.2)] shrink-0">
          <Compass className="w-12 h-12 text-amber-500 animate-[spin_12s_linear_infinite]" />
        </div>
        
        <h1 className="text-3xl font-bold tracking-widest text-white mb-2 uppercase text-center shrink-0">
          True North
        </h1>
        
        <div className="w-full max-w-sm mb-8 mt-2 p-4 bg-slate-800/50 border border-slate-700 rounded-2xl italic text-center shadow-lg shrink-0">
          <p className="text-slate-300 text-sm leading-relaxed mb-2">"{quoteOfDay.text}"</p>
          <p className="text-amber-500/80 text-xs font-semibold uppercase tracking-wider">— {quoteOfDay.author}</p>
        </div>
        
        <form onSubmit={handleSubmit} className="w-full max-w-xs space-y-4 shrink-0">
          <div className="relative group">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500 group-focus-within:text-amber-500 transition-colors" />
            <input 
              type="password" 
              placeholder="Enter Access Code"
              className="w-full bg-slate-800/80 border border-slate-700 rounded-xl py-3 pl-11 pr-4 text-white focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-all placeholder-slate-500 shadow-inner"
              value={code}
              onChange={(e) => { setCode(e.target.value); setError(''); }}
            />
          </div>
          
          {error && (
            <div className="flex items-center text-red-400 text-sm bg-red-400/10 p-3 rounded-lg border border-red-400/20 animate-pulse">
              <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
              {error}
            </div>
          )}
          
          <button 
            type="submit"
            className="w-full bg-amber-600 hover:bg-amber-500 text-white font-semibold py-3 rounded-xl tracking-wider uppercase transition-colors shadow-lg active:scale-[0.98]"
          >
            Begin Journey
          </button>
        </form>
      </div>
    </div>
  );
};

// --- Main Feed Screen Component ---
const FeedScreen = ({ user, onLogout }) => {
  const [posts, setPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [newText, setNewText] = useState('');
  const [newMedia, setNewMedia] = useState(null);
  const [commentInputs, setCommentInputs] = useState({});
  const [isEditingName, setIsEditingName] = useState(false);
  const fileInputRef = useRef(null);

  // Setup/Fetch device-saved Explorer Name
  const [explorerName, setExplorerName] = useState(() => {
    return localStorage.getItem('explorerName') || `Explorer-${Math.floor(1000 + Math.random() * 9000)}`;
  });

  // Track Native Notification permission state
  const [notificationPermission, setNotificationPermission] = useState(
    typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : 'denied'
  );

  // Ref to prevent initial historical posts from triggering alerts when launching the app
  const isInitialLoad = useRef(true);
  // Keep posts in ref for instant, accurate comparison inside real-time listener closures
  const postsRef = useRef([]);

  // Keep explorerName saved on the device
  const saveExplorerName = (newName) => {
    const formatted = newName.trim() || 'Anonymous Explorer';
    setExplorerName(formatted);
    localStorage.setItem('explorerName', formatted);
  };

  // Helper: Trigger browser-level notification
  const triggerBrowserNotification = (title, body) => {
    if (!("Notification" in window)) return;
    if (Notification.permission === "granted") {
      try {
        new Notification(title, {
          body: body,
          icon: "/favicon.ico"
        });
      } catch (e) {
        console.error("Failed to fire browser notification:", e);
      }
    }
  };

  // Setup Web Notification Perms
  const requestNotificationPermission = async () => {
    if (!("Notification" in window)) {
      alert("This device or mobile browser does not support Web Push notifications.");
      return;
    }
    try {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
    } catch (e) {
      console.error("Error asking for notification permission:", e);
    }
  };

  // RULE 3 & RULE 1 & RULE 2: Read database in Real-Time
  useEffect(() => {
    if (!user) return;

    // Use Rule 1 strict paths for shared artifacts
    const postsCollection = collection(db, 'artifacts', appId, 'public', 'data', 'posts');

    const unsubscribe = onSnapshot(postsCollection, (snapshot) => {
      const postsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Rule 2: Sort posts cleanly in Javascript memory instead of complex compound queries
      postsList.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      
      // Notification Detection: Run comparisons after initial fetch
      if (!isInitialLoad.current) {
        // Detect a brand-new post
        const currentNewest = postsList[0];
        const previousNewest = postsRef.current[0];
        
        if (currentNewest && (!previousNewest || currentNewest.id !== previousNewest.id)) {
          // Verify it was made by another explorer, not us!
          if (currentNewest.author !== explorerName) {
            triggerBrowserNotification(
              `New Log from ${currentNewest.author}`,
              currentNewest.text || "Shared a photo with the journal"
            );
          }
        }

        // Detect a brand-new reply on existing posts
        postsList.forEach(post => {
          const oldPost = postsRef.current.find(p => p.id === post.id);
          if (oldPost && post.comments?.length > (oldPost.comments?.length || 0)) {
            const newestComment = post.comments[post.comments.length - 1];
            if (newestComment.author !== explorerName) {
              triggerBrowserNotification(
                `${newestComment.author} replied on a log`,
                newestComment.text
              );
            }
          }
        });
      } else {
        isInitialLoad.current = false;
      }

      // Sync refs and states
      postsRef.current = postsList;
      setPosts(postsList);
      setLoadingPosts(false);
    }, (error) => {
      console.error("Firestore database subscription error:", error);
      setLoadingPosts(false);
    });

    return () => unsubscribe();
  }, [user, explorerName]);

  // Handle Photo Upload with built-in compression to satisfy Firestore < 1MB limit
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.src = reader.result;
        img.onload = () => {
          // Downscale canvas to fit perfectly under firestore size constraints
          const canvas = document.createElement('canvas');
          const MAX_WIDTH = 700;
          const MAX_HEIGHT = 700;
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Compress quality slightly so uploading large phone images is extremely fast & small
          const compressedBase64 = canvas.toDataURL('image/jpeg', 0.65);
          setNewMedia(compressedBase64);
        };
      };
      reader.readAsDataURL(file);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Write new post straight into Cloud Database
  const handlePost = async () => {
    if (!user || (!newText.trim() && !newMedia)) return;

    const postPayload = {
      author: explorerName,
      text: newText.trim(),
      media: newMedia,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      createdAt: Date.now(),
      comments: []
    };

    try {
      const postsCollection = collection(db, 'artifacts', appId, 'public', 'data', 'posts');
      await addDoc(postsCollection, postPayload);
      setNewText('');
      setNewMedia(null);
    } catch (error) {
      console.error("Error creating document in database:", error);
    }
  };

  // Sync new comment instantly in Firestore
  const handleAddComment = async (postId) => {
    if (!user) return;
    const commentText = commentInputs[postId];
    if (!commentText?.trim()) return;

    const newComment = {
      id: Date.now(),
      author: explorerName,
      text: commentText.trim(),
      time: 'Just now',
      createdAt: Date.now()
    };

    try {
      const targetPost = posts.find(p => p.id === postId);
      if (targetPost) {
        const postDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'posts', postId);
        const updatedComments = [...(targetPost.comments || []), newComment];
        
        await updateDoc(postDocRef, {
          comments: updatedComments
        });
      }
    } catch (error) {
      console.error("Failed to post comment to cloud:", error);
    }

    setCommentInputs(prev => ({ ...prev, [postId] : '' }));
  };

  return (
    <>
      {/* Header */}
      <header className="shrink-0 bg-slate-900 border-b border-slate-800 p-4 flex items-center justify-between shadow-md z-10">
         <div className="flex items-center space-x-3">
           <Compass className="w-7 h-7 text-amber-500" />
           <h1 className="text-lg font-bold tracking-widest text-slate-100 uppercase">True North</h1>
         </div>
         <div className="flex items-center space-x-1.5">
           {/* Notification Setup Bell */}
           <button
             onClick={requestNotificationPermission}
             className={`p-2 rounded-full transition-colors ${
               notificationPermission === 'granted'
                 ? 'text-emerald-400 hover:bg-emerald-500/10'
                 : 'text-slate-400 hover:text-amber-400 hover:bg-slate-800'
             }`}
             title={notificationPermission === 'granted' ? "Notifications Active" : "Enable Notifications"}
           >
             {notificationPermission === 'granted' ? (
               <Bell className="w-5 h-5" />
             ) : (
               <BellOff className="w-5 h-5 text-slate-500 hover:text-amber-500" />
             )}
           </button>
           <button 
              onClick={onLogout} 
              className="p-2 text-slate-400 hover:text-amber-400 hover:bg-slate-800 rounded-full transition-colors"
              title="Lock App"
           >
             <Lock className="w-5 h-5" />
           </button>
         </div>
      </header>

      {/* Explorer Profile Setup Banner */}
      <div className="shrink-0 bg-slate-800/80 border-b border-slate-700/40 px-4 py-2 flex items-center justify-between text-xs text-slate-400">
        <div className="flex items-center space-x-2 w-full">
          <span>Writing as:</span>
          {isEditingName ? (
            <div className="flex items-center space-x-1 flex-1">
              <input
                type="text"
                value={explorerName}
                maxLength={20}
                onChange={(e) => setExplorerName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') setIsEditingName(false); }}
                className="bg-slate-950 border border-amber-500/50 rounded px-2 py-0.5 text-slate-200 focus:outline-none w-full max-w-[150px]"
                autoFocus
              />
              <button 
                onClick={() => { saveExplorerName(explorerName); setIsEditingName(false); }}
                className="p-1 bg-amber-600 rounded text-white"
              >
                <Check className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <div className="flex items-center space-x-1.5">
              <strong className="text-amber-500 font-medium">{explorerName}</strong>
              <button onClick={() => setIsEditingName(true)} className="text-slate-500 hover:text-slate-300">
                <Edit2 className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>
        <span className="text-[10px] bg-emerald-500/10 text-emerald-400 px-2 py-0.5 rounded-full border border-emerald-400/20 flex items-center shrink-0">
          <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full mr-1.5 animate-ping" />
          Cloud Active
        </span>
      </div>

      {/* Feed Area */}
      <main className="flex-1 overflow-y-auto p-4 space-y-6 bg-slate-900/50">
        {/* Simple Notification Opt-In Banner if Not Yet Configured */}
        {notificationPermission !== 'granted' && (
          <div className="bg-slate-800/60 border border-amber-500/10 rounded-2xl p-4 flex flex-col items-center text-center space-y-3 shadow-inner">
            <Bell className="w-6 h-6 text-amber-500 animate-bounce" />
            <div>
              <h4 className="text-sm font-semibold text-slate-200">Stay in the Loop</h4>
              <p className="text-xs text-slate-400 mt-1 leading-relaxed">Enable system notifications to see in real-time when other explorers log findings or reply.</p>
            </div>
            <button
              onClick={requestNotificationPermission}
              className="bg-amber-600/20 hover:bg-amber-600 border border-amber-500/30 hover:border-amber-500 text-amber-400 hover:text-white px-4 py-1.5 rounded-lg text-xs font-semibold tracking-wider uppercase transition-all duration-200"
            >
              Enable Notifications
            </button>
          </div>
        )}

        {loadingPosts ? (
          <div className="flex flex-col items-center justify-center h-full text-slate-400 space-y-3">
            <Compass className="w-8 h-8 text-amber-500 animate-spin" />
            <p className="text-sm tracking-wider">Syncing with True North database...</p>
          </div>
        ) : posts.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-slate-500 space-y-4">
            <MapIcon className="w-12 h-12 opacity-20" />
            <p className="tracking-wide text-center">Your journey begins here.<br/>Be the first to record findings.</p>
          </div>
        ) : (
          posts.map(post => (
            <article key={post.id} className="bg-slate-800 rounded-2xl p-5 shadow-lg border border-slate-700/50">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center border border-amber-500/30">
                  <MapIcon className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-200 tracking-wide">{post.author}</h3>
                  <p className="text-[10px] text-slate-400 uppercase tracking-wider">{post.time || 'Logged'}</p>
                </div>
              </div>
              
              {post.text && (
                <p className="text-slate-300 mb-4 whitespace-pre-wrap leading-relaxed">
                  {post.text}
                </p>
              )}
              
              {post.media && (
                <div className="rounded-xl overflow-hidden border border-slate-700/50 bg-slate-900">
                  <img 
                    src={post.media} 
                    alt="Post attachment" 
                    className="w-full h-auto object-cover max-h-80" 
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&q=80&w=800";
                    }} 
                  />
                </div>
              )}

              {/* Comments Section */}
              <div className="mt-4 pt-4 border-t border-slate-700/50">
                {post.comments && post.comments.length > 0 && (
                  <div className="space-y-3 mb-4">
                    {post.comments.map(comment => (
                      <div key={comment.id} className="bg-slate-900/50 rounded-lg p-3 border border-slate-700/30">
                        <div className="flex justify-between items-baseline mb-1">
                          <span className="text-xs font-semibold text-slate-300">{comment.author}</span>
                          <span className="text-[10px] text-slate-500">{comment.time}</span>
                        </div>
                        <p className="text-sm text-slate-400">{comment.text}</p>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Add Comment Input */}
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    placeholder="Reply to this entry..."
                    className="flex-1 bg-slate-900 border border-slate-700 rounded-lg py-2 px-3 text-sm text-slate-200 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 placeholder-slate-600 shadow-inner"
                    value={commentInputs[post.id] || ''}
                    onChange={(e) => setCommentInputs(prev => ({ ...prev, [post.id]: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleAddComment(post.id);
                    }}
                  />
                  <button
                    onClick={() => handleAddComment(post.id)}
                    disabled={!(commentInputs[post.id]?.trim())}
                    className="p-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-amber-500 rounded-lg transition-colors border border-slate-700 shadow-sm"
                    title="Post Comment"
                  >
                    <MessageCircle className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </article>
          ))
        )}
      </main>

      {/* Compose Area */}
      <footer className="shrink-0 bg-slate-800 border-t border-slate-700 p-4 shadow-[0_-10px_20px_rgba(0,0,0,0.2)]">
         <div className="flex flex-col gap-3">
            {/* Image Preview */}
            {newMedia && (
              <div className="relative w-24 h-24 rounded-xl overflow-hidden border border-amber-500/30 shadow-inner">
                <img src={newMedia} alt="Upload preview" className="w-full h-full object-cover" />
                <button 
                  onClick={() => setNewMedia(null)} 
                  className="absolute top-1 right-1 p-1 bg-black/60 hover:bg-black/80 rounded-full text-white transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            
            {/* Primary Input Row */}
            <div className="flex items-end space-x-3">
              <input 
                type="file"
                accept="image/*"
                className="hidden"
                ref={fileInputRef}
                onChange={handleImageUpload}
              />
              
              <button 
                onClick={() => fileInputRef.current?.click()}
                className={`p-3 rounded-xl transition-colors shadow-sm ${
                  newMedia 
                  ? 'bg-amber-500 text-slate-900' 
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600 border border-slate-600'
                }`}
                title="Upload Photo"
              >
                <ImageIcon className="w-5 h-5" />
              </button>
              
              <textarea 
                placeholder="Log something amazing..."
                className="flex-1 bg-slate-900 border border-slate-700 rounded-xl p-3 text-slate-200 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 resize-none max-h-32 min-h-[48px] shadow-inner"
                rows={1}
                value={newText}
                onChange={(e) => setNewText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                    handlePost();
                  }
                }}
              />
              
              <button 
                onClick={handlePost}
                disabled={!newText.trim() && !newMedia}
                className="p-3 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 disabled:hover:bg-amber-600 text-white rounded-xl transition-all shadow-md active:scale-[0.95]"
                title="Post Entry"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
         </div>
      </footer>
    </>
  );
};